import { InvalidOperationException, NotSupportedException } from '@mosaik/core';
import { IconFonts } from '../../Models/IconFonts';
import { IIconFont } from '../../Models/Interfaces/IIconFont';
import { IIconService } from './Interfaces/IIconService';

/**
 * @public
 */
export class IconService implements IIconService {
  private readonly _cachedSvgIcons: Set<string>;
  private readonly _customs: Map<string, string>;
  private _default: IIconFont;
  private _svgContainer: HTMLElement | null;

  public constructor() {
    this._customs = new Map<string, string>();
    this._default = IconFonts.material;
    this._cachedSvgIcons = new Set<string>();
    this._svgContainer = null;
  }

  public get defaultIconFont(): IIconFont {
    return this._default;
  }
  public set defaultIconFont(value: IIconFont) {
    this._default = value;
  }

  public registerIconFont(iconFont: IIconFont): this {
    this._customs.set(iconFont.alias, iconFont.className || iconFont.alias);
    return this;
  }

  public registerIconFonts(iconFonts: Array<IIconFont>): this {
    iconFonts.forEach(x => this.registerIconFont(x));
    return this;
  }

  public getFromAlias(alias: string): string {
    return this._customs.get(alias) || alias;
  }

  public addSvgIconFromPath(data: string, name: string, fontSet?: string): this {
    if (name && data) {
      const path = this.createSvgPath(data);
      const svg = this.createSvg();
      svg.appendChild(path);
      this.cacheSvg(svg, name, fontSet);
    } else {
      throw new InvalidOperationException('You should provide at least `data` and `name` to register an svg icon.');
    }
    return this;
  }

  public addSvgIconFromUrl(): IIconService {
    throw new NotSupportedException('This method is currently not implemented.');
  }

  public addSvgIconFromXml(xml: string, name: string, fontSet?: string): IIconService {
    if (name && xml) {
      const svg = this.createSvg(xml);
      this.cacheSvg(svg, name, fontSet);
    } else {
      throw new InvalidOperationException('You should provide at least `xml` and `name` to register an svg icon.');
    }
    return this;
  }

  public isSvgIconCached(iconName: string, fontSet: string = ''): boolean {
    const iconKey = `${fontSet}_${iconName}`;
    return this._cachedSvgIcons.has(iconKey);
  }

  private cacheSvg(svg: SVGElement, name: string, fontSet: string = ''): void {
    if (svg && name) {
      this.ensureSvgContainerCreated();
      const iconKey = `${fontSet}_${name}`;
      // @todo: make the viewBoxsize as variable from outside
      const viewBoxSize = 24;

      svg.setAttribute('id', iconKey);
      svg.setAttribute('fit', '');
      svg.setAttribute('width', '100%');
      svg.setAttribute('height', '100%');
      svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
      svg.setAttribute('viewBox', svg.getAttribute('viewBox') || `0 0 ${viewBoxSize} ${viewBoxSize}`);
      svg.setAttribute('focusable', 'false');

      if (this._cachedSvgIcons.has(iconKey)) {
        const oldChild = this._svgContainer!.querySelector(`svg[id='${iconKey}']`);
        if (oldChild) {
          this._svgContainer!.removeChild(oldChild);
        }
      }

      this._svgContainer!.appendChild(svg);
      this._cachedSvgIcons.add(iconKey);
    }
  }

  private createSvgPath(data: string): SVGPathElement {
    // tslint:disable-next-line:no-http-string
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttributeNS(null, 'd', data);
    return path;
  }

  private createSvg(xml?: string): SVGElement {
    const div = document.createElement('DIV');
    div.innerHTML = xml || '<svg></svg>';
    const svg = div.querySelector('svg') as SVGElement;
    if (!svg) {
      throw new InvalidOperationException('<svg> tag not found.');
    }
    return svg;
  }

  private ensureSvgContainerCreated(): void {
    if (!this._svgContainer) {
      this._svgContainer = document.documentElement!.querySelector('.dynamic-svg-container');
      if (!this._svgContainer) {
        this._svgContainer = document.createElement('DIV');
        this._svgContainer.classList.add('dynamic-svg-container');
        document.documentElement!.appendChild(this._svgContainer);
      }
    }
  }
}
